import {Component, ElementRef, Input, OnDestroy, OnInit} from '@angular/core';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {Field, getMapSatelliteLayer, getMapStreetLayer, Route, RouteLoader, RouteParams, RouteRenderer} from 'shared';
import {LayerGroup, Map, Polygon, tileLayer} from 'leaflet';
import {delay, first} from 'rxjs/operators';
import {FieldService} from '../../stores/field.service';
import {NgElement, WithProperties} from '@angular/elements';
import {MapFieldPopupComponent} from '../../map-fields/map-field-popup/map-field-popup.component';
import * as L from 'leaflet';

@Component({
  selector: 'app-tractors-history-map',
  templateUrl: './tractors-history-map.component.html',
  styleUrls: ['./tractors-history-map.component.scss']
})
export class TractorsHistoryMapComponent implements OnInit, OnDestroy {

  private fieldsLayer: LayerGroup;

  @Input()
  renderer: RouteRenderer;
  private rendererSub: Subscription;

  @Input()
  loader: RouteLoader;
  private loaderSub: Subscription;
  route: Route;

  @Input()
  routeParams: RouteParams;

  isLoading = true;

  private map: Map;
  mapReady$ = new ReplaySubject<Map>(1);

  @Input()
  onResize: Observable<number[]>;
  private onResizeSub: Subscription;

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  options = {
    layers: this.layers.map(l => l.layer),
    zoom: 7,
    minZoom: 0,
    maxZoom: 21,
    maxNativeZoom: 21,
    preferCanvas: false,
    center: [33.626756199999996, -7.4964748]
  };

  constructor(
    public host: ElementRef,
    private fieldService: FieldService,
  ) {
  }

  ngOnInit(): void {
    this.onResizeSub = this.onResize.subscribe((size) => {
      this.mapReady$.pipe(first()).subscribe(() => {
        this.map.invalidateSize({
          animate: true,
        });
      });
    });

    this.rendererSub = this.renderer.loading$.subscribe(isLoading => {
      setTimeout(() => {
        this.isLoading = isLoading;
      }, 20);
    });

    this.loaderSub = this.loader.loading$.subscribe(isLoading => {
      if (this.loader.isEmpty()) {
        return;
      }
      if (isLoading) {
        this.clean();
        return;
      }
      this.mapReady$.pipe(first(), delay(30)).subscribe(() => {
        this.renderer.render(this.map, this.loader.route, this.routeParams);
      });
    });
  }

  onMapReady(map: Map) {
    this.map = map;
    this.mapReady$.next(map);
    L.control.scale({imperial: false}).addTo(map);
    this.addFields();
    this.isLoading = false;
  }

  private addFields() {
    // Create map group
    this.fieldsLayer = new LayerGroup<Polygon>();
    this.fieldsLayer.addTo(this.map);

    this.addFieldsRecursive(this.fieldService.dataSource
      .items());
  }

  private addFieldsRecursive(list: (Field & { items })[]) {

    if (!list) {
      return;
    }

    list.forEach(f => {
      if (f.hasOwnProperty('items')) {
        this.addFieldsRecursive(f.items);
      } else {
        this.addField(f);
      }
    });
  }

  ngOnDestroy(): void {
    this.onResizeSub.unsubscribe();
    this.rendererSub.unsubscribe();
    this.loaderSub.unsubscribe();
  }

  private clean() {
    this.renderer.reset();
  }

  private addField(f: Field) {
    const polygon = f.createLayer({
      fillColor: f.color,
      weight: 1,
    });

    let popupEl: NgElement & WithProperties<MapFieldPopupComponent> = null;

    polygon.bindPopup(() => {
      if (popupEl === null) {
        popupEl = document.createElement('field-popup') as any;
      }
      popupEl.field = f;
      return popupEl;
    }, {
      offset: [0, -20],
      minWidth: 300,
      className: 'field-map-popup',
    });

    polygon.bindTooltip(f.name, {
      direction: 'center',
      opacity: 0.6,
    });

    this.fieldsLayer.addLayer(polygon);
  }
}
